/**
 * System Settings–style building blocks (owner, 2026-10-17): grouped inset cards with hairline rows, label/value rows,
 * app tiles, centred notices, loading skeletons, and the organization row with its switch. Settings → Connected apps
 * and the MCP consent page are built from these, so the two screens a person sees for one connection look alike.
 */
import type { ReactNode } from 'react'
import { Building2, KeyRound, User } from 'lucide-react'
import { ToggleSwitch } from './ToggleSwitch'

/** Hairline dividers between a group's rows, in the theme's border colour. */
export const GROUP_ROWS = 'divide-y divide-[color:var(--border-primary)]'
/** The grouped card surface. */
export const GROUP_CARD = 'themed-bg-secondary rounded-xl border themed-border'

/** A stable tint per client host, so one app always gets the same tile. */
const TILE_TINTS = [
  'from-orange-400 to-rose-500',
  'from-sky-400 to-indigo-500',
  'from-emerald-400 to-teal-600',
  'from-violet-400 to-fuchsia-500',
  'from-amber-400 to-orange-600',
]
const TILE_SIZES = {
  md: 'h-9 w-9 rounded-[10px] text-base',
  lg: 'h-16 w-16 rounded-[18px] text-2xl',
} as const

/**
 * An app's tile: the logo a platform admin uploaded for a VERIFIED app (served by Papan — `logoUrl`), else its initial
 * on a tint chosen by its host. Never a logo the app declared about itself.
 */
export function AppTile({ name, host, size = 'md', logoUrl }: { name: string; host: string; size?: keyof typeof TILE_SIZES; logoUrl?: string | null }) {
  if (logoUrl) {
    return (
      <span aria-hidden className={`${TILE_SIZES[size]} shrink-0 inline-flex items-center justify-center overflow-hidden bg-white shadow-sm border themed-border`}>
        <img src={logoUrl} alt="" className="h-full w-full object-contain p-[12%]" draggable={false} />
      </span>
    )
  }
  let h = 0
  for (const ch of host || name) h = (h * 31 + ch.charCodeAt(0)) >>> 0
  return (
    <span aria-hidden className={`${TILE_SIZES[size]} shrink-0 inline-flex items-center justify-center bg-gradient-to-br ${TILE_TINTS[h % TILE_TINTS.length]} text-white font-semibold shadow-sm`}>
      {(name.trim()[0] ?? '?').toUpperCase()}
    </span>
  )
}

/** One grouped card: hairline-separated rows, an optional small-caps header and a footnote beneath. */
export function Group({ header, footer, children, testId }: { header?: ReactNode; footer?: ReactNode; children: ReactNode; testId?: string }) {
  return (
    <section className="space-y-1.5" data-testid={testId}>
      {header && <h3 className="px-4 text-[11px] font-medium uppercase tracking-wider themed-text-muted">{header}</h3>}
      <div className={`${GROUP_CARD} overflow-hidden ${GROUP_ROWS}`}>{children}</div>
      {footer && <div className="px-4 text-xs leading-relaxed themed-text-muted">{footer}</div>}
    </section>
  )
}

/** A label-left, value-right row. */
export function InfoRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-2.5 text-sm">
      <span className="themed-text">{label}</span>
      <span className="themed-text-muted text-right">{value}</span>
    </div>
  )
}

/** A row with a leading icon and text — a bullet inside a group. */
export function IconRow({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3 px-4 py-2.5 text-sm">
      <span aria-hidden className="mt-0.5 shrink-0">{icon}</span>
      <span className="themed-text leading-snug">{children}</span>
    </div>
  )
}

export function SkeletonRows({ rows = 2 }: { rows?: number }) {
  return (
    <div className={`${GROUP_CARD} ${GROUP_ROWS} animate-pulse`} aria-hidden>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-3">
          <span className="h-9 w-9 rounded-[10px] themed-bg-tertiary" />
          <span className="flex-1 space-y-1.5">
            <span className="block h-3 w-32 rounded themed-bg-tertiary" />
            <span className="block h-2.5 w-48 rounded themed-bg-tertiary" />
          </span>
        </div>
      ))}
    </div>
  )
}

/** A centred message in a card — an empty list, a finished action, something that is gone. */
export function Notice({ icon, title, body, tone = 'neutral', role, testId }: {
  icon: ReactNode; title?: string; body: ReactNode; tone?: 'neutral' | 'success'; role?: 'status' | 'alert'; testId?: string
}) {
  return (
    <div className={`${GROUP_CARD} px-6 py-10 text-center`} role={role} data-testid={testId}>
      <span className={`mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full ${tone === 'success' ? 'bg-emerald-500/15' : 'themed-bg-tertiary'}`}>
        {icon}
      </span>
      {title && <p className="text-sm font-medium themed-text">{title}</p>}
      <p className={`text-sm ${title ? 'mt-1 themed-text-muted' : 'themed-text'}`}>{body}</p>
    </div>
  )
}

export interface OrgRowIdentity {
  tenantId: string
  name: string
  type: string
  mode: string
  emails: string[]
  mcpOff?: boolean
  /** #1297: the org doesn't let connected AI apps use its keybook secrets. */
  mcpSecretsOff?: boolean
}

/**
 * #1297 — the connection's keybook switch for ONE org, shown under its row while the org is on: may app runs this
 * connection starts there use the org's keybook secrets (masked in all it gets back)? Off by default. The CALLER
 * decides what is allowed: an org whose policy forbids it can't be turned on (it can still be turned off). The row
 * is one short line; what the switch means is said ONCE, under the group (`SecretsNote`), not repeated per org.
 */
export interface OrgSecretsSwitch {
  on: boolean
  disabled: boolean
  onToggle: (on: boolean) => void
  labels: { title: string; policyOff: string }
  testId: string
}

/** The group footnote that explains the per-org keybook switches (#1297) — said once, below every org's switch. */
export function SecretsNote({ children }: { children: ReactNode }) {
  return (
    <span className="mt-2 flex items-start gap-1.5">
      <KeyRound aria-hidden className="mt-0.5 h-3 w-3 shrink-0" />
      <span>{children}</span>
    </span>
  )
}

/**
 * One organization a connection may act in: its icon, name and email (or why it can't be chosen), a switch, and —
 * when on and the person has several emails there — which one it acts as. The CALLER decides what is allowed
 * (`disabled`): the consent page never offers an org that turned connections off; a connection's page lets one it
 * already has be removed.
 */
export function OrgSwitchRow({ identity, email, disabled, dimmed, onToggle, onEmail, labels, testId, offTestId, secrets }: {
  identity: OrgRowIdentity
  /** The email it acts as there; undefined = off. */
  email: string | undefined
  disabled: boolean
  /** Shown faded: the org can't be chosen (as opposed to a passing `busy`). */
  dimmed: boolean
  onToggle: (on: boolean) => void
  onEmail: (email: string) => void
  labels: { noDrive: string; orgOff: string; asEmail: string }
  testId: string
  offTestId: string
  secrets?: OrgSecretsSwitch
}) {
  const on = email !== undefined
  const Icon = identity.type === 'personal' ? User : Building2
  return (
    <div className={`px-4 py-3 ${dimmed ? 'opacity-60' : ''}`}>
      <div className="flex items-center gap-3">
        <span aria-hidden className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full themed-bg-tertiary">
          <Icon className="h-4 w-4 themed-text-muted" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="themed-text text-sm block truncate">{identity.name}</span>
          {identity.mode !== 'full'
            ? <span className="themed-text-muted text-xs block">{labels.noDrive}</span>
            : identity.mcpOff
            ? <span className="text-amber-400 text-xs block" data-testid={offTestId}>{labels.orgOff}</span>
            : identity.emails.length === 1 ? <span className="themed-text-muted text-xs block truncate">{identity.emails[0]}</span> : null}
        </span>
        <span data-testid={testId} className="contents">
          <ToggleSwitch size="md" checked={on} disabled={disabled} onChange={onToggle} label={identity.name} />
        </span>
      </div>
      {identity.mode === 'full' && !identity.mcpOff && identity.emails.length > 1 && on && (
        <label className="mt-2 ml-11 flex items-center gap-2 text-xs themed-text-muted">
          {labels.asEmail}
          <select
            className="themed-input border rounded-md px-2 py-1 text-xs min-w-0 flex-1"
            value={email}
            disabled={disabled}
            onChange={(e) => onEmail(e.target.value)}
          >
            {identity.emails.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
        </label>
      )}
      {secrets && identity.mode === 'full' && on && (
        <div className="mt-3 ml-11 flex items-center gap-2.5 border-t themed-border pt-2.5" data-testid={secrets.testId}>
          <KeyRound aria-hidden className="h-3.5 w-3.5 shrink-0 themed-text-muted" />
          <span className="min-w-0 flex-1 text-[13px]">
            <span className="themed-text block">{secrets.labels.title}</span>
            {identity.mcpSecretsOff && <span className="block text-xs text-amber-400">{secrets.labels.policyOff}</span>}
          </span>
          <ToggleSwitch size="sm" checked={secrets.on} disabled={secrets.disabled} onChange={secrets.onToggle} label={`${secrets.labels.title} — ${identity.name}`} />
        </div>
      )}
    </div>
  )
}
